(() => {
  'use strict';
  const configElement = document.getElementById('nspire-cart-upsells-config');
  if (!configElement || window.nspireCartUpsellsInstalled) return;
  const config = JSON.parse(configElement.textContent);
  window.nspireCartUpsellsInstalled = true;
  const rootPath = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const sessionKey = 'nspire-cart-upsells-recency-v1';
  const originalFetch = window.fetch;
  const productCache = new Map();
  let mappingPromise;
  let activeRoot;
  let readyRoot;
  let refreshTimer;
  let sequence = 0;
  let recency = [];
  const bareId = id => String(id || '').split('/').pop();
  try {
    const saved = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
    recency = Array.isArray(saved) ? saved.filter(item => /^\d+$/.test(item.id) && Number.isFinite(item.time)).slice(0, 30) : [];
  } catch (_) { recency = []; }

  function saveRecency() {
    try { sessionStorage.setItem(sessionKey, JSON.stringify(recency)); } catch (_) { /* Storage is optional. */ }
  }

  function recordAdded(products, time) {
    for (const product of products) {
      const id = bareId(product.product_id || product.productId);
      if (!/^\d+$/.test(id)) continue;
      const current = recency.find(item => item.id === id);
      if (current && current.time > time) continue;
      recency = recency.filter(item => item.id !== id);
      recency.push({ id, time });
    }
    recency.sort((a, b) => b.time - a.time);
    recency = recency.slice(0, 30);
    saveRecency();
    scheduleRefresh();
  }

  window.fetch = function (...args) {
    const request = args[0];
    const address = typeof request === 'string' || request instanceof URL ? String(request) : request?.url;
    let cartAdd = false;
    let graphqlVariants = [];
    try {
      const url = new URL(address, location.href);
      cartAdd = url.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
      if (url.origin === location.origin && /\/graphql\.json$/.test(url.pathname) && typeof args[1]?.body === 'string') {
        const payload = JSON.parse(args[1].body);
        if (/\bcartLinesAdd\b/.test(payload.query || '')) graphqlVariants = (payload.variables?.lines || []).map(line => line.merchandiseId);
      }
    } catch (_) { cartAdd = false; }
    const time = Date.now() + (++sequence / 1000);
    const result = originalFetch.apply(this, args);
    if (cartAdd || graphqlVariants.length) {
      result.then(response => {
        if (response.ok) response.clone().json().then(data => {
          if (cartAdd) recordAdded(data.items || [data], time);
          else {
            const lines = data.data?.cartLinesAdd?.cart?.lines;
            const nodes = lines?.nodes || lines?.edges?.map(edge => edge.node) || [];
            recordAdded(nodes.filter(line => graphqlVariants.includes(line.merchandise?.id))
              .map(line => ({ productId: line.merchandise.product?.id })), time);
          }
        }).catch(() => {});
      }).catch(() => {});
    }
    return result;
  };

  async function readJson(url) {
    const response = await originalFetch.call(window, url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Upsell resource ${response.status}`);
    return response.json();
  }

  function getMapping() {
    mappingPromise ||= readJson(config.mappingUrl).catch(error => { mappingPromise = undefined; throw error; });
    return mappingPromise;
  }

  function getProduct(handle) {
    const cached = productCache.get(handle);
    if (cached && Date.now() - cached.time < 60000) return cached.promise;
    const promise = readJson(`${rootPath}products/${encodeURIComponent(handle)}.js`).catch(error => {
      productCache.delete(handle);
      throw error;
    });
    productCache.set(handle, { time: Date.now(), promise });
    return promise;
  }

  function normalizeProduct(product, cart) {
    const nativeId = (id, type) => String(cart.items[0]?.[type === 'Product' ? 'productId' : 'variantId']).startsWith('gid://')
      ? `gid://shopify/${type}/${id}` : id;
    const options = (product.options || []).map((option, index) => typeof option === 'string'
      ? { name: option, position: index + 1, values: [...new Set(product.variants.map(variant => variant.options[index]))] }
      : option);
    const variants = product.variants.map(variant => ({
      id: nativeId(variant.id, 'ProductVariant'),
      title: variant.title,
      available: Boolean(variant.available),
      priceCents: Number(variant.price),
      compareAtCents: variant.compare_at_price ? Number(variant.compare_at_price) : null,
      image: variant.featured_image?.src || null,
      selectedOptions: options.map((option, index) => ({ name: option.name, value: variant.options[index] }))
    }));
    const first = variants.find(variant => variant.available && variant.priceCents > 0);
    if (!first) return null;
    return {
      productId: nativeId(product.id, 'Product'),
      variantId: first.id,
      title: product.title,
      image: first.image || product.featured_image,
      currency: cart.currency || 'EUR',
      priceCents: first.priceCents,
      compareAtCents: first.compareAtCents,
      available: true,
      options,
      variants
    };
  }

  const adapter = {
    orientation: 'horizontal',
    hideInCart: true,
    async byCart(cart) {
      if (!cart?.items?.length) { recency = []; saveRecency(); return []; }
      try {
        const mapping = await getMapping();
        const groups = new Map(mapping.groups.map(group => [group.key, group]));
        const sources = new Map(mapping.groups.flatMap(group => group.products.map(product => [product.id, { group, product }])));
        const inCart = new Set(cart.items.map(item => bareId(item.productId)));
        const recencyById = new Map(recency.map(item => [item.id, item.time]));
        const items = cart.items.map((item, index) => ({ item, index, time: recencyById.get(bareId(item.productId)) || 0 }))
          .sort((a, b) => b.time - a.time || a.index - b.index);
        const result = [];
        const usedModels = new Set();
        const visitedSources = new Set();
        for (const { item } of items) {
          const source = sources.get(bareId(item.productId));
          if (!source || visitedSources.has(source.product.id)) continue;
          visitedSources.add(source.product.id);
          for (const suggestion of source.group.upsells) {
            if (result.length >= Math.min(3, mapping.limit)) return result;
            if (usedModels.has(suggestion.model)) continue;
            const target = groups.get(suggestion.model);
            if (!target) continue;
            const candidates = target.products.filter(product => !inCart.has(product.id)
              && (!suggestion.differentColor || product.color !== source.product.color));
            const rank = product => product.color === source.product.color ? 0 : product.color === 'black' ? 1 : 2;
            candidates.sort((a, b) => rank(a) - rank(b));
            for (const candidate of candidates) {
              let product;
              try { product = normalizeProduct(await getProduct(candidate.handle), cart); } catch (_) { continue; }
              if (!product || inCart.has(bareId(product.productId)) || result.some(selected => selected.productId === product.productId)) continue;
              result.push(product);
              usedModels.add(suggestion.model);
              break;
            }
          }
        }
        return result;
      } catch (error) {
        console.warn('Nspire cart recommendations unavailable', error);
        return [];
      }
    }
  };

  function decorate() {
    const root = document.getElementById('sternify-cart-root');
    if (!root) return;
    root.setAttribute('data-nspire-cro-upsells', '');
    const heading = root.querySelector('.sd-recs__title');
    if (heading && heading.textContent !== config.heading) heading.textContent = config.heading;
    for (const card of root.querySelectorAll('.sd-recs__card')) {
      const title = card.querySelector('.sd-recs__name')?.textContent || '';
      for (const select of card.querySelectorAll('.sd-recs__option-select')) {
        const label = config.sizeFor.replace('__PRODUCT__', title);
        if (select.getAttribute('aria-label') !== label) select.setAttribute('aria-label', label);
      }
    }
    if (root !== activeRoot && typeof window.SternifyCart?.setRecommendationsAdapter === 'function') {
      activeRoot = root;
      window.SternifyCart.setRecommendationsAdapter(adapter);
    }
    if (root !== readyRoot && root.querySelector('.sd-line__title')?.textContent && activeRoot === root) {
      readyRoot = root;
      scheduleRefresh();
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (activeRoot?.isConnected) window.SternifyCart?.setRecommendationsAdapter(adapter);
      decorate();
    }, 30);
  }

  const observer = new MutationObserver(decorate);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('sternify:cart:updated', scheduleRefresh);
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('pagehide', () => { clearTimeout(refreshTimer); observer.disconnect(); });
  window.addEventListener('pageshow', () => observer.observe(document.documentElement, { childList: true, subtree: true }));
  decorate();
})();
